import React from 'react';
import { notification } from 'antd';
import { useDispatch } from 'react-redux';
import { AppDispatch } from './store';
import { addNotification } from './slices/appInfoSlice';
import { NotificationStatus, NotificationType } from './common/types';

export const Notifications: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [api, contextHolder] = notification.useNotification({
    maxCount: 1,
  });
  const dispatch = useDispatch<AppDispatch>();

  window.openNotification = (
    status: NotificationStatus,
    title: string,
    message?: string,
    notificationIndex?: number,
    addToPanel: boolean = true,
    linkConfig?: {
      url?: string;
      onClick?: () => void;
    }
  ) => {
    const notificationStatusMap: Record<NotificationStatus, NotificationType> = {
      [NotificationStatus.Success]: 'success',
      [NotificationStatus.Error]: 'error',
      [NotificationStatus.Warning]: 'warning',
      [NotificationStatus.Info]: 'info',
      [NotificationStatus.InProgress]: 'info',
    };

    const notificationStatus: NotificationType =
      notificationStatusMap[status] || "info";

    const handleClick = () => {
      if (linkConfig?.onClick) {
        try {
          linkConfig.onClick();
        } catch (error) {
          console.error("Error in notification click handler:", error);
          setTimeout(() => {
            try {
              if (linkConfig?.onClick) linkConfig.onClick();
            } catch (retryError) {
              console.error("Error in notification retry handler:", retryError);
              api.error({
                message: "Action Failed",
                description:
                  "Could not complete the requested action. Please try again.",
                placement: "bottomLeft",
              });
            }
          }, 500);
        }
      } else if (linkConfig?.url) {
        window.open(linkConfig.url, "_blank");
      }
    };

    const description = linkConfig ? (
      <span
        onClick={handleClick}
        style={{
          cursor: "pointer",
          color: "#1890ff",
          textDecoration: "underline",
        }}
      >
        {message}
      </span>
    ) : (
      message
    );

    const notificationConfig: any = {
      message: title,
      description: description || message,
      placement: "bottomLeft",
      duration: 0,
    };

    if (linkConfig?.onClick || linkConfig?.url) {
      notificationConfig.onClick = handleClick;
    }

    api[notificationStatus](notificationConfig);

    let index = -1; // Initialize with invalid index

    if (notificationIndex !== undefined) {
      index = dispatch(addNotification({ title, status, message, linkConfig }, notificationIndex));
    } else if (addToPanel) {
      index = dispatch(addNotification({ title, status, message, linkConfig }));
    }

    return index;  // always returns number (0 or higher)
  };

  return (
    <>
      {contextHolder}
      {children}
    </>
  );
};

declare global {
  interface Window {
    openNotification: (
      status: NotificationStatus,
      title: string,
      message?: string,
      notificationIndex?: number,
      addToPanel?: boolean,
      linkConfig?: {
        url?: string;
        onClick?: () => void;
      }
    ) => number;
  }
}